import { useAuth0 } from '@auth0/auth0-react';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook.tsx';
import { queryKeys } from '@queries/keys.ts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { removeEmptyStrings } from '@utils/form-validator.util.ts';
import {
  CompanyCreateDto,
  CompanyPatchDto,
  CompanySelectDto,
  UsersNestedSelectFromCompanyDto,
} from '../types/api.types.ts';
import { queryClient } from './client.ts';
import { useGetCurrentUser, useGetUserSelectedCompanyId } from './users.query.ts';

export const useCreateCompanyProfile = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  const onError = (error: Error) => {
    console.log('Error submitting company profile:', error);
  };

  const onSuccess = (data: CompanySelectDto) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.users.getCurrentUser });
    queryClient.setQueryData(queryKeys.companyProfile.getCompanyProfile(data.id), data);
    queryClient.invalidateQueries({
      queryKey: queryKeys.companyProfile.getCompanyProfiles,
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.technology.getTechnologies,
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.assessments.getAssessments(),
    });
  };

  return useMutation<CompanySelectDto, Error, CompanyCreateDto>({
    mutationFn: async (formData) => {
      const cleanedData = removeEmptyStrings(formData);
      return await apiPost<CompanySelectDto>({
        path: '/v1/innovator/companies',
        body: cleanedData,
      });
    },
    onError,
    onSuccess,
  });
};

export const useGetCompanyUsers = () => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const selectedCompanyId = useGetUserSelectedCompanyId();
  const enabled = Boolean(isAuthenticated && selectedCompanyId);

  return useQuery<UsersNestedSelectFromCompanyDto, ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      return await apiGet<UsersNestedSelectFromCompanyDto>({
        path: `/v1/innovator/companies/users`,
      });
    },
    queryKey: enabled ? queryKeys.userCompanies.getCompanyUsers : queryKeys.placeholders.default,
  });
};

export const useUpdateCompany = () => {
  const { apiPatch } = useApi({ showToastOnError: false });
  const { isIntel } = useGetCurrentUser();

  const onSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.users.getCurrentUser,
    });
  };

  return useMutation<CompanySelectDto, ApiErrorWrapper, CompanyPatchDto & { answerId?: string }>({
    mutationFn: async (formData) => {
      const cleanedData = removeEmptyStrings(formData, true);
      cleanedData.invites = undefined;
      return await apiPatch<CompanySelectDto>({
        path: isIntel ? '/v1/intel/organization' : '/v1/innovator/companies',
        body: cleanedData,
      });
    },
    onSuccess,
  });
};

export const useGetCurrentCompany = () => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const selectedCompanyId = useGetUserSelectedCompanyId();
  const enabled = Boolean(isAuthenticated && selectedCompanyId);

  return useQuery<CompanySelectDto, ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      return await apiGet<CompanySelectDto>({ path: `/v1/innovator/companies/${selectedCompanyId}` });
    },
    queryKey:
      enabled && selectedCompanyId
        ? queryKeys.companyProfile.getCompanyProfile(selectedCompanyId)
        : queryKeys.placeholders.default,
  });
};
